/**
 * isiXhosa: Inbox
 *
 * What this file covers: Conversations with clients on every channel: replying, notes, assigning, taking over
 * from the chatbot, canned replies.
 *
 * Tone: Quick and practical, for people answering clients all day. Short labels. When something cannot be sent,
 * say why and what to do, in one sentence.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const inbox: Catalog = {
  'inbox.title': 'Ibhokisi yokungena',
  'inbox.empty': 'Akukho ncoko okwangoku: qhoboshela inani uze uvumele umamkeli akuthumelele umyalezo.',
  'inbox.select': 'Khetha incoko ukuze ufunde imbali uze uphendule.',
  'inbox.noMessages': 'Akukho miyalezo okwangoku',
  'inbox.loadingMessages': 'Ilayisha imiyalezo…',
  'inbox.assign': 'Yabela i-arhente',
  'inbox.unassigned': 'Ayabelwanga',
  'inbox.assigned': 'Ulwabiwo luhlaziyiwe.',
  'inbox.lock': 'Tshixa',
  'inbox.locked': 'Incoko itshixelelwe wena.',
  'inbox.lockedBy': 'Itshixwe ngu',
  'inbox.anotherAgent': 'enye i-arhente',
  'inbox.send': 'Thumela',
  'inbox.newline': 'ukuze uqale umgca omtsha',
  'inbox.canned.noMatch': 'Akukho mpendulo zilungiselelweyo zifanayo.',
  'inbox.canned.manage': 'Lawula iimpendulo ezilungiselelweyo',
  'inbox.canned.navigate': 'khangela',
  'inbox.canned.insert': 'faka',
  'inbox.canned.close': 'vala',
  'inbox.tags.title': 'Iithegi',
  'inbox.tags.add': 'Yongeza ithegi',
  'inbox.tags.placeholder': 'Igama lethegi…',
  'inbox.tags.suggestions': 'Iithegi ezikhoyo',
  'inbox.transfer': 'Dlulisela',
  'inbox.transfer.title': 'Dlulisela incoko',
  'inbox.transfer.agent': 'Dlulisela ku',
  'inbox.transfer.note': 'Inqaku le-arhente eyamkelayo (ngokuzikhethela)',
  'inbox.transfer.noteprefix': 'Idluliselwe ku',
  'inbox.transfer.keeps': 'Amanqaku neethegi zihlala nencoko.',
  'inbox.transfer.done': 'Incoko idluliselwe.',
  'inbox.sla.firstReply': 'Impendulo yokuqala',
  'inbox.sla.resolve': 'Sombulula',
  'inbox.sla.left': 'eseleyo',
  'inbox.sla.over': 'sigqithile',
  'inbox.sla.breached': 'I-SLA yaphuliwe',
  'inbox.sla.met': 'I-SLA ifezekisiwe',
  'inbox.sla.due': 'Ifanele ngo',
  'inbox.notes.title': 'Amanqaku angaphakathi',
  'inbox.notes.empty': 'Akukho manqaku okwangoku.',
  'inbox.notes.agent': 'I-arhente',
  'inbox.notes.add': 'Yongeza inqaku',
  'inbox.lock.heldBy': 'Itshixwe ngu',
  'inbox.lock.until': 'de kube',
  'inbox.lock.mine': 'Uphethe le ncoko',
  'inbox.lock.release': 'Khulula',
  'inbox.lock.released': 'Isitshixo sikhululiwe.',
  'inbox.lock.readOnly': 'Ukufunda kuphela ngelixa enye i-arhente iphendula.',
  'inbox.resolve': 'Sombulula',
  'inbox.reopen': 'Vula kwakhona',
  'inbox.stateChanged': 'Incoko ihlaziyiwe.',
  'inbox.state.pending': 'Ilindile',
  'inbox.state.open': 'Ivuliwe',
  'inbox.state.resolved': 'Isonjululwe',
  'inbox.state.archived': 'Igciniwe',
  'canned.title': 'Iimpendulo ezilungiselelweyo',
  'canned.new': 'Impendulo entsha',
  'canned.edit': 'Hlela impendulo',
  'canned.shortcut': 'Indlela emfutshane',
  'canned.titleField': 'Isihloko',
  'canned.body': 'Umyalezo',
  'canned.language': 'Ulwimi',
  'canned.empty': 'Akukho mpendulo zilungiselelweyo okwangoku.',
  'canned.saved': 'Impendulo elungiselelweyo igciniwe.',
  'canned.deleted': 'Impendulo elungiselelweyo icimiwe.',
  'canned.updated': 'Ihlaziyiwe',
  'inbox.assignFailed': 'Ukwabela kusilele',
  'inbox.lockFailed': 'Ukutshixa kusilele',
  'inbox.notes.addFailed': 'Ayikwazanga ukongeza inqaku.',
  'inbox.releaseFailed': 'Ukukhulula kusilele',
  'inbox.sendFailed': 'Ukuthumela kusilele',
  'inbox.tags.addFailed': 'Ayikwazanga ukongeza ithegi.',
  'inbox.tags.removeFailed': 'Ayikwazanga ukususa ithegi.',
  'inbox.transfer.transferFailed': 'Ukudlulisela kusilele',
  'inbox.updateFailed': 'Ukuhlaziya kusilele',
  'inbox.conversations': 'Iincoko',
  'inbox.filters.more': 'Ezinye izihluzi',
  'inbox.queue.open': 'Ivuliwe',
  'inbox.queue.resolved': 'Isonjululwe',
  'inbox.showingOf': 'Kuboniswa {{shown}} kwezingu-{{total}}',
  'inbox.sla': 'SLA',
  'inbox.tags': 'Iithegi',
  'inbox.assignedTo.label': 'Yabelwe ku',
  'inbox.composer.emoji': 'Faka i-emoji',
  'inbox.composer.canned': 'Faka impendulo elungiselelweyo',
  'inbox.composer.hint': 'Cofa u-Enter ukuthumela, Shift+Enter umgca omtsha',
  'inbox.message.sent': 'Kuthunyelwe',
  'inbox.notes.private': 'Yabucala',
  'inbox.quickReplies.title': 'Iimpendulo ezikhawulezayo',
  'inbox.quickReplies.search': 'Khangela iimpendulo',
  'inbox.quickReplies.empty': 'Akukho mpendulo zilungiselelweyo okwangoku.',
  'inbox.tip': 'Ingcebiso: chwetheza u-/ kwibhokisi yempendulo ufumane iimpendulo ezilungiselelweyo, uze ucofe u-Enter ukuthumela.',
  'inbox.sla.onTrack': 'Kuhamba kakuhle',
  'inbox.sla.dueSoon': 'Sekusondele',
  'inbox.replyWindow': 'Ifestile yokuphendula',
  'inbox.newMessage': 'Umyalezo omtsha',
  'inbox.message.failed': 'Ayihanjiswanga',
  'inbox.message.failed.code': 'Ayihanjiswanga (ikhowudi {{code}})',
  'inbox.message.failed.WINDOW_CLOSED': 'Ayihanjiswanga: abakubhalelanga kwiiyure ezingama-24 ezidlulileyo, ngoko oku bekufuna itemplethi eyamkelweyo.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE': 'Ayihanjiswanga: i-WhatsApp iyalile ngenxa yengxaki yentlawulo kwicala lethu, hayi elakho. Siyayilungisa.',
  'inbox.message.failed.UNDELIVERABLE': 'Ayihanjiswanga: i-WhatsApp ayikwazanga ukuyisa kula nombolo.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED': 'Ayihanjiswanga: inombolo othumele ngayo ayibhaliswanga kwi-WhatsApp.',
  'inbox.message.failed.MEDIA_NOT_FETCHED': 'Ayihanjiswanga: i-WhatsApp ayikwazanga ukulanda ifayile eqhotyoshelweyo.',
  'inbox.message.failed.META_REFUSED': 'Ayihanjiswanga: i-WhatsApp iyamkele yaza ayayihambisa.',
  'inbox.message.failed.notCharged': 'Akubizwanga ngale.',
  'inbox.message.template.marker': 'Itemplethi · {{name}}',
  'inbox.message.template.noWording': 'Amagama alo myalezo wetemplethi khange abhalwe phantsi xa uthunyelwa.',
  'inbox.message.template.noWording.named': 'Amagama e-“{{name}}” khange abhalwe phantsi xa oku kuphuma.',
  'inbox.message.template.buttons': 'Amaqhosha kulo myalezo',
  'inbox.message.template.header.IMAGE': 'Umfanekiso phezulu',
  'inbox.message.template.header.VIDEO': 'Ividiyo phezulu',
  'inbox.message.template.header.DOCUMENT': 'Ifayile phezulu',
  'inbox.message.template.header.LOCATION': 'Indawo phezulu',
  'inbox.message.failed.WINDOW_CLOSED.next': 'Endaweni yoko thumela itemplethi evunyiweyo. Xa sele bephendule ungabhala ngokukhululekileyo kwakhona kwezinye iiyure ezingama-24.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.next': 'Akufuneki wenze nto kwa-Meta. Imiyalezo iza kuphinda idlule sakuba siyilungisile.',
  'inbox.message.failed.UNDELIVERABLE.next': 'Jonga ukuba inombolo ichanekile nokuba isebenzisa i-WhatsApp. Ukuba ichanekile, umntu usenokuba uyivalele imiyalezo evela kwishishini lakho.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED.next': 'Vula iiNombolo uze uqhagamshele kwakhona le nombolo. Ukuba icela ikhowudi, siya kukukhokela kuqinisekiso.',
  'inbox.message.failed.MEDIA_NOT_FETCHED.next': 'Yithumele kwakhona ngefayile elayishwe apha endaweni yokukhonkxwa, okanye ujonge ukuba ikhonkco livulekile kwaye lisasebenza.',
  'inbox.message.failed.META_REFUSED.next': 'Zama kwakhona kwimizuzu embalwa. Ukuba kuyaqhubeka, sixelele size sikujonge.',
  'inbox.details.title': 'Iinkcukacha zomyalezo',
  'inbox.details.open': 'Kwenzeke ntoni kulo myalezo',
  'inbox.details.close': 'Vala iinkcukacha zomyalezo',
  'inbox.details.error': 'Iinkcukacha zalo myalezo azikwazanga ukulayishwa.',
  'inbox.details.timeline': 'Okwenzekileyo',
  'inbox.details.timeline.sent': 'Ithunyelwe',
  'inbox.details.timeline.received': 'Ifunyenwe',
  'inbox.details.timeline.delivered': 'Ihanjisiwe',
  'inbox.details.timeline.read': 'Ifundiwe',
  'inbox.details.timeline.failed': 'Ayihanjiswanga',
  'inbox.details.timeline.later': '{{duration}} kamva',
  'inbox.details.read.absent': 'Akukho siqinisekiso sokufunda. I-WhatsApp ithumela esinye kuphela xa umamkeli evule iziqinisekiso zokufunda, ngoko oku kunokuhlala kungenanto nokuba sele bewufundile umyalezo wakho.',
  'inbox.details.delivered.pending': 'I-WhatsApp ayikaqinisekisi ukuhanjiswa okwangoku.',
  'inbox.details.why': 'Kutheni ingafikanga',
  'inbox.details.why.metaCode': 'Ikhowudi ye-WhatsApp',
  'inbox.details.why.metaWords': 'I-WhatsApp ithe',
  'inbox.details.cost': 'Ixabise malini',
  'inbox.details.cost.unknown': 'Asikwazanga ukufunda incwadi yeengxelo yalo myalezo, ngoko asikwazi ukuthi ixabise malini.',
  'inbox.details.cost.rate': 'Ixabiso',
  'inbox.details.cost.worked': 'Ibalwe njenge',
  'inbox.details.cost.workedValue': '{{units}} × {{rate}}',
  'inbox.details.cost.chargedAt': 'Ihlawulisiwe',
  'inbox.details.cost.refunded': 'Kubuyiselwe i-{{amount}} kuba i-WhatsApp ingawuhambisanga lo myalezo.',
  'inbox.details.sent': 'Okuthunyelweyo',
  'inbox.details.template': 'Itemplethi',
  'inbox.details.language': 'Ulwimi',
  'inbox.details.category': 'Udidi',
  'inbox.details.wording': 'Amagama',
  'inbox.details.wording.recovered': 'Afunyenwe kwitemplethi njengoko imi ngoku, ngoko anokwahluka koko kwathunyelwayo.',
  'inbox.details.wording.lost': 'Akhange abhalwe phantsi xa oku kuthunyelwa, kwaye itemplethi ayisekho.',
  'inbox.details.params': 'Amaxabiso afakiweyo',
  'inbox.details.param.header': 'Umxholo',
  'inbox.details.param.body': 'Umyalezo',
  'inbox.details.param.button': 'Iqhosha',
  'inbox.details.file.name': 'Igama lefayile',
  'inbox.details.file.type': 'Uhlobo lwefayile',
  'inbox.details.file.size': 'Ubungakanani befayile',
  'inbox.details.where': 'Apho iye khona',
  'inbox.details.whereFrom': 'Apho ivela khona',
  'inbox.details.to': 'Kuya ku',
  'inbox.details.from': 'Ivela ku',
  'inbox.details.sentBy': 'Ithunyelwe ngu',
  'inbox.details.sentBy.you': 'Wena',
  'inbox.details.sentBy.teammate': 'Ilungu leqela',
  'inbox.details.sentBy.bot': 'I-chatbot yakho',
  'inbox.details.sentBy.broadcast': 'Usasazo',
  'inbox.details.sentBy.platform': 'RepletoryReach',
  'inbox.details.support': 'Ukufumana uncedo',
  'inbox.details.metaId.hint': 'Caphula oku ukuba ubuza i-WhatsApp ngalo myalezo.',
  'inbox.details.copy': 'Khuphela',
  'inbox.details.copied': 'Ikhutshelwe',
  'inbox.channel.whatsapp': 'WhatsApp',
  'inbox.channel.messenger': 'Messenger',
  'inbox.channel.instagram': 'Instagram',
  'inbox.window.closed.body.messenger': 'I-Messenger ihambisa impendulo kuphela phakathi kweeyure ezingama-24 emva komyalezo wokugqibela womntu, kwaye elo xesha lidlulile. Impendulo yakho iya kubafikelela xa bebhala kwakhona.',
  'inbox.window.closed.body.instagram': 'I-Instagram ihambisa impendulo kuphela phakathi kweeyure ezingama-24 emva komyalezo wokugqibela womntu, kwaye elo xesha lidlulile. Impendulo yakho iya kubafikelela xa bebhala kwakhona.',
  'inbox.send.notMetered': 'Umyalezo uthunyelwe, kodwa asikwazanga ukurekhoda ukuba ubiza malini. Inkxaso yaziswe; akukho nto irhafiswe kabini.',
  'inbox.sidebar.title': 'Iisetingi zencoko',
  'inbox.sidebar.toggle': 'Iisetingi',
  'inbox.sidebar.close': 'Vala iisetingi zencoko',
  'inbox.sidebar.details': 'Iinkcukacha',
  'inbox.sidebar.shortcuts': 'Izinqumleli zekhibhodi',
  'inbox.notes.visibility': 'Ibonwa liqela lakho kuphela',
  'inbox.lock.hint': 'Tshixa le ncoko ukuze kungabikho mntu wumbi uphendulayo ngoxa uphendula',
  'inbox.shortcuts.send': 'Thumela impendulo',
  'inbox.shortcuts.newline': 'Qala umgca omtsha',
  'inbox.shortcuts.canned': 'Vula iimpendulo ezilungiselelweyo',
  'inbox.shortcuts.pick': 'Hamba kwiimpendulo ezilungiselelweyo',
  'inbox.shortcuts.insert': 'Faka impendulo ekhethiweyo',
  'inbox.shortcuts.dismiss': 'Vala uluhlu lweempendulo ezilungiselelweyo',
  'inbox.shortcuts.key.enter': 'Enter',
  'inbox.shortcuts.key.shiftEnter': 'Shift+Enter',
  'inbox.shortcuts.key.slash': '/',
  'inbox.shortcuts.key.arrows': '↑ ↓',
  'inbox.shortcuts.key.enterTab': 'Enter okanye Tab',
  'inbox.shortcuts.key.esc': 'Esc',
  'inbox.bot.label': 'I-bot',
  'inbox.bot.paused': 'I-bot inqunyanyisiwe',
  'inbox.bot.active': 'I-bot iyaphendula',
  'inbox.bot.resumesAt': 'I-bot iphinde iphendule ukususela ngo-{{time}}, ngaphandle kokuba iqela liphendule phambi koko.',
  'inbox.bot.untilResolved': 'I-bot ihlala ithe cwaka de le ncoko isonjululwe okanye uyiqhubekise.',
  'inbox.bot.activeHint': 'I-bot iphendula imiyalezo emitsha. Ukuba uzenzela impendulo, iyinqumamisa iiyure ezingama-24.',
  'inbox.bot.resume': 'Qhubekisa i-bot',
  'inbox.bot.pause': 'Nqumamisa i-bot',
  'inbox.bot.resumed': 'I-bot iza kuphinda iphendule kule ncoko.',
  'inbox.bot.pausedToast': 'I-bot inqunyanyisiwe kule ncoko.',
  'inbox.bot.failed': 'I-bot ayikwazanga ukutshintshwa kule ncoko.',
  'inbox.block.label': 'Umthengi',
  'inbox.block.blocked': 'Uvaliwe',
  'inbox.block.notBlocked': 'Akavalwanga',
  'inbox.block.action': 'Vala umthengi',
  'inbox.block.unblock': 'Vula',
  'inbox.block.title': 'Vala lo mthengi?',
  'inbox.block.body': 'I-bot ayizukumphendula kwaye akukho mntu weqela lakho onokumbhalela de umvule. Imiyalezo yakhe isagcinwa apha.',
  'inbox.block.reason': 'Isizathu',
  'inbox.block.reasonHint': 'Yeqela lakho. Umthengi akasiboni.',
  'inbox.block.alsoWhatsapp': 'Vala nale nombolo ku-WhatsApp',
  'inbox.block.alsoWhatsappHint': 'Abasayi kukwazi ukuthumela umyalezo kwinombolo yakho kwaphela. I-WhatsApp ivala kuphela umntu obhale kwiiyure ezingama-24 ezidlulileyo.',
  'inbox.block.confirm': 'Vala',
  'inbox.block.done': 'Umthengi uvaliwe.',
  'inbox.block.doneWhatsapp': 'Umthengi uvaliwe apha naku-WhatsApp.',
  'inbox.block.whatsappFailed': 'Uvaliwe apha, kodwa i-WhatsApp ayiyivalanga inombolo: {{error}}',
  'inbox.block.unblocked': 'Umthengi uvuliwe.',
  'inbox.block.failed': 'Umthengi akakwazanga ukuvalwa okanye ukuvulwa.',
  'inbox.block.bar': 'Uvale lo mthengi. I-bot ayimphenduli kwaye iimpendulo zicinyiwe.',
  'inbox.block.since': 'Uvalwe {{time}}',
  'inbox.block.onWhatsapp': 'Uvalwe naku-WhatsApp'
};

export default inbox;
